import { HttpContext, HttpContextToken, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, Subject, Subscription, of } from 'rxjs';
import { HttpStatusCode } from '../../shared/enums/http-status-codes.enum';

export enum CONTEXT_TOKEN_VALUE {
  GET_EQUIPMENT = 1,
  GET_STANDALONE_TASK = 2,
  SHIFT_STANDALONE_TASK = 3,
  SAVE_CUSTOM_AT_OFFSET = 4,
  TRANSFER_EQUIPMENT = 5,
}

type EventSelectorSubscription = (sender: Observable<HttpErrorResponse>) => Subscription;

@Injectable({
  providedIn: 'root',
})
export class RequestErrorService {
  private readonly contextToken = new HttpContextToken<number>(() => -1);

  private sendersMap: Map<number, Subject<HttpErrorResponse>[]> = new Map();

  public getHttpContext(value: CONTEXT_TOKEN_VALUE, context?: HttpContext): HttpContext {
    const updatedContext = context ?? new HttpContext();
    updatedContext.set(this.contextToken, value);
    return updatedContext;
  }

  public selectEventSender(value: CONTEXT_TOKEN_VALUE, cbEventSelector: EventSelectorSubscription): void {
    if (typeof cbEventSelector === 'function') {
      const sender = this.createNewSender(value);
      this.initEventSelectorSubscription(value, cbEventSelector, sender);
    }
  }

  public handleError(error: HttpErrorResponse, req: HttpRequest<unknown>): Observable<HttpErrorResponse | never> {
    const tokenValue = req.context.get(this.contextToken);
    const senders = this.sendersMap.get(tokenValue);

    if (this.shouldHandle(tokenValue, error, senders)) {
      senders?.forEach((sender) => sender.next(error));
      return EMPTY;
    }

    return of(error);
  }

  private createNewSender(value: CONTEXT_TOKEN_VALUE): Subject<HttpErrorResponse> {
    const sender = new Subject<HttpErrorResponse>();
    const senders = this.sendersMap.get(value) ?? [];
    senders.push(sender);
    this.sendersMap.set(value, senders);
    return sender;
  }

  private initEventSelectorSubscription(
    value: CONTEXT_TOKEN_VALUE,
    cbEventSelector: EventSelectorSubscription,
    sender: Subject<HttpErrorResponse>,
  ): void {
    cbEventSelector(sender).add(() => {
      const senders = this.sendersMap.get(value);
      const index = senders?.indexOf(sender) ?? -1;
      if (index > -1 && senders) {
        senders[index].complete();
        senders.splice(index, 1);
      }
    });
  }

  private shouldHandle(tokenValue: number, error: HttpErrorResponse, senders?: Subject<HttpErrorResponse>[]): boolean {
    switch (tokenValue) {
      case CONTEXT_TOKEN_VALUE.GET_EQUIPMENT:
        return error.status === HttpStatusCode.NOT_FOUND && !!senders?.length;
      case CONTEXT_TOKEN_VALUE.GET_STANDALONE_TASK:
        return error.status === HttpStatusCode.INTERNAL_SERVER_ERROR && !!senders?.length;
      default:
        return false;
    }
  }
}
